import { useCart } from '../stores/providers/cart.provider';
import {
    type AddConfigurableProductsToCartMutation,
    type AddSimpleProductsToCartMutation,
    type UpdateCartItemsMutation,
    type CartItemUpdateInput,
    type ConfigurableProductCartItemInput,
    type ConfigurableVariant,
    type SimpleProductCartItemInput,
    type Maybe,
    type RemoveItemFromCartMutation,
    useAddConfigurableProductsToCartMutation,
    useAddSimpleProductsToCartMutation,
    useCreateEmptyCartMutation,
    useRemoveItemFromCartMutation,
    useUpdateCartItemsMutation,
} from '../types/front.graphql';
import type { ApolloError, FetchResult } from '@apollo/client';
import { type ProductModel } from '@kidslabs/module-catalog';
import { logSentry, useMagentoGraphQlUrl, storeCodeEmitter } from '@kidslabs/module-core';
import { cartSessionManager } from '../contracts';

const INACTIVE_CART_MESSAGE = 'The cart is not active.';

export function useCreateEmptyCart() {
    const [createEmptyWebCartMutation, { loading, error, data }] = useCreateEmptyCartMutation();
    const { doSetCartId, setCartLoading } = useCart();

    async function createEmptyCart() {
        return createEmptyWebCartMutation({
            onCompleted: value => {
                const newCartId = value.createEmptyCart ?? '';
                doSetCartId(newCartId);
                setCartLoading(false);

                // Persist new cart ID to visitor session (Redis)
                if (newCartId) {
                    const storeCode = storeCodeEmitter.getCurrentStoreCode();
                    cartSessionManager.setCartId(storeCode, newCartId).catch(console.error);
                }
            },
            onError: err => {
                setCartLoading(false);
                console.error('error on createEmptyCart -> ', err);
            },
        });
    }

    return {
        createEmptyCart,
        loading,
        error,
        data,
    };
}

export interface AddToCartItem {
    product: ProductModel;
    quantity: number;
    variation?: Maybe<ConfigurableVariant>;
}

interface UseAddToCartReturnType {
    addToCartServerSideAsync: (
        cartId: string,
        product: ProductModel,
        quantity: number,
        variation?: Maybe<ConfigurableVariant>
    ) => Promise<(AddConfigurableProductsToCartMutation | AddSimpleProductsToCartMutation | null | undefined)[]>;
    addToCartServerSide: (
        cartId: string,
        product: ProductModel,
        quantity: number,
        variation?: Maybe<ConfigurableVariant>
    ) => Promise<(AddConfigurableProductsToCartMutation | AddSimpleProductsToCartMutation | null | undefined)[]>;
    addMultipleItemsToCartServerSide: (cartId: string, items: AddToCartItem[]) => void;
    addConfigurableProductsToCartResult: AddConfigurableProductsToCartMutation | null | undefined;
    addConfigurableToCartLoading: boolean;
    error: ApolloError | undefined;
    addSimpleProductsToCartResult: AddSimpleProductsToCartMutation | null | undefined;
    addSimpleToCartLoading: boolean;
    simpleError: ApolloError | undefined;
}

interface UseRemoveItemFromCartType {
    removeItemFromCartServerSide: (cartId: string, cartItemId: number) => Promise<FetchResult>;
    data: RemoveItemFromCartMutation | null | undefined;
    loading: boolean;
    error: ApolloError | undefined;
}

export function useAddToCart(): UseAddToCartReturnType {
    const magentoGraphqlApiEndpointPublic = useMagentoGraphQlUrl();
    const [addSimpleProductsToCartMutation, { data: simpleProductResult, loading: simpleLoading, error: simpleError }] =
        useAddSimpleProductsToCartMutation();
    const [addConfigurableProductsToCartMutation, { data, loading: configurableLoading, error }] =
        useAddConfigurableProductsToCartMutation();

    const { doResetCurrentCart } = useCart();

    const onCompleted = (cartId: string) => {
        console.log('completed cart ID -> ', cartId);
    };
    const onError = (err: ApolloError, cartId: string) => {
        if (err.message === INACTIVE_CART_MESSAGE) {
            doResetCurrentCart();
        }
        logSentry({
            name: 'cart-mutations',
            transactionName: 'addToCart',
            error: err,
            extra: {
                cartId,
            },
        });
    };

    const addMultipleItemsToCart = (
        cartId: string,
        items: AddToCartItem[]
    ): Promise<(AddConfigurableProductsToCartMutation | AddSimpleProductsToCartMutation | null | undefined)[]> => {
        const processItems = async () => {
            const configurableProductItems: ConfigurableProductCartItemInput[] = [];
            const simpleProductItems: SimpleProductCartItemInput[] = [];
            let configurableResult: AddConfigurableProductsToCartMutation | undefined | null;
            let simpleResult: AddSimpleProductsToCartMutation | undefined | null;

            items.forEach(item => {
                const { product, quantity, variation } = item;
                if (product.__typename === 'ConfigurableProduct') {
                    configurableProductItems.push({
                        data: {
                            quantity: quantity,
                            sku: variation?.product?.sku ?? '',
                        },
                        parent_sku: product?.sku ?? '',
                    });
                } else {
                    simpleProductItems.push({
                        data: {
                            quantity: quantity,
                            sku: product.sku ?? '',
                        },
                    });
                }
            });

            if (configurableProductItems.length) {
                const result = await addConfigurableProductsToCartMutation({
                    variables: {
                        input: {
                            cart_id: cartId,
                            cart_items: configurableProductItems,
                        },
                    },
                    context: {
                        uri: magentoGraphqlApiEndpointPublic,
                    },
                });
                configurableResult = result?.data;
            }

            if (simpleProductItems.length) {
                const result = await addSimpleProductsToCartMutation({
                    variables: {
                        input: {
                            cart_id: cartId,
                            cart_items: simpleProductItems,
                        },
                    },
                    context: {
                        uri: magentoGraphqlApiEndpointPublic,
                    },
                });
                simpleResult = result?.data;
            }

            return [configurableResult, simpleResult];
        };

        return new Promise((resolve, reject) => {
            processItems()
                .then(results => resolve(results))
                .catch(err => reject(err));
        });
    };

    const addToCartServerSideAsync = (
        cartId: string,
        product: ProductModel,
        quantity: number,
        variation?: Maybe<ConfigurableVariant>
    ): Promise<(AddConfigurableProductsToCartMutation | AddSimpleProductsToCartMutation | null | undefined)[]> => {
        const itemToAdd: AddToCartItem = {
            product,
            quantity,
            variation,
        };
        return addMultipleItemsToCart(cartId, [itemToAdd]);
    };

    const addToCartServerSide = (
        cartId: string,
        product: ProductModel,
        quantity: number,
        variation?: Maybe<ConfigurableVariant>
    ): Promise<(AddConfigurableProductsToCartMutation | AddSimpleProductsToCartMutation | null | undefined)[]> => {
        return new Promise((resolve, reject) => {
            addToCartServerSideAsync(cartId, product, quantity, variation)
                .then(result => {
                    onCompleted(cartId);
                    resolve(result);
                })
                .catch(err => {
                    logSentry({
                        name: 'cart-mutations',
                        transactionName: 'addToCartServerSide',
                        error: err,
                    });
                    onError(err, cartId);
                    reject(err);
                });
        });
    };

    const addMultipleItemsToCartServerSide = (cartId: string, items: AddToCartItem[]) => {
        addMultipleItemsToCart(cartId, items)
            .then(() => {
                onCompleted(cartId);
            })
            .catch(err => {
                logSentry({
                    name: 'cart-mutations',
                    transactionName: 'addMultipleItemsToCartServerSide',
                    error: err,
                });
                onError(err, cartId);
            });
    };

    return {
        addToCartServerSideAsync,
        addToCartServerSide,
        addMultipleItemsToCartServerSide,
        addConfigurableProductsToCartResult: data,
        addConfigurableToCartLoading: configurableLoading,
        error,
        addSimpleProductsToCartResult: simpleProductResult,
        addSimpleToCartLoading: simpleLoading,
        simpleError,
    };
}

export function useUpdateCartItem() {
    const [updateCartItemsMutation, { data, loading, error }] = useUpdateCartItemsMutation();

    const onCompleted = (cartId: string) => {
        console.log('completed cart ID -> ', cartId);
    };
    const onError = (err: ApolloError, cartId: string) => {
        console.log('error on updateCartItem -> ', err);
        logSentry({
            name: 'cart-mutations',
            transactionName: 'updateCartItem',
            error: err,
            extra: {
                cartId,
            },
        });
    };

    const updateMultipleCartItems = (cartId: string, items: CartItemUpdateInput[]) => {
        return updateCartItemsMutation({
            variables: {
                input: {
                    cart_id: cartId,
                    cart_items: items,
                },
            },
        });
    };

    const updateCartItemServerSideAsync = (cartId: string, cartItemId: number, quantity: number) => {
        const cartItems: CartItemUpdateInput[] = [
            {
                cart_item_id: cartItemId,
                quantity: quantity,
            },
        ];

        return updateMultipleCartItems(cartId, cartItems);
    };

    const updateCartItemServerSide = (
        cartId: string,
        cartItemId: number,
        quantity: number
    ): Promise<FetchResult<UpdateCartItemsMutation>> => {
        return new Promise((resolve, reject) => {
            updateCartItemServerSideAsync(cartId, cartItemId, quantity)
                .then((result: FetchResult<UpdateCartItemsMutation>) => {
                    onCompleted(cartId);
                    resolve(result);
                })
                .catch(err => {
                    onError(err, cartId);
                    logSentry({
                        name: 'cart-mutations',
                        transactionName: 'updateCartItemServerSide',
                        error: err,
                        extra: {
                            cartId,
                            cartItemId,
                            quantity,
                        },
                    });
                    reject(err);
                });
        });
    };

    const updateMultipleCartItemsServerSide = (cartId: string, items: CartItemUpdateInput[]) => {
        updateMultipleCartItems(cartId, items)
            .then(() => {
                onCompleted(cartId);
            })
            .catch(err => {
                onError(err, cartId);
                logSentry({
                    name: 'cart-mutations',
                    transactionName: 'updateMultipleCartItemsServerSide',
                    error: err,
                    extra: {
                        cartId,
                        items,
                    },
                });
            });
    };

    return {
        updateCartItemServerSide,
        updateCartItemServerSideAsync,
        updateMultipleCartItemsServerSide,
        data,
        loading,
        error,
    };
}

export function useRemoveItemFromCart(): UseRemoveItemFromCartType {
    const [removeItemFromCartMutation, { data, loading, error }] = useRemoveItemFromCartMutation();
    const removeItemFromCartServerSide = (cartId: string, cartItemId: number) => {
        return removeItemFromCartMutation({
            variables: {
                input: {
                    cart_id: cartId,
                    cart_item_id: cartItemId,
                },
            },
        });
    };

    return {
        removeItemFromCartServerSide,
        data,
        loading,
        error,
    };
}
